import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import IconBlock from '@component/common/block/icon-block';
import TextBlock from '@component/common/block/text-block';
import BottomContainer from '@component/common/architecture/bottom-container';
import IconTextBlock from '@component/common/block/icon-text-block';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { v4 as uuid4 } from 'uuid';
import Gap from '@component/common/gap';
import Line from '@component/common/line';
import WorkspaceCanvas from '@component/gridgio/workspace/workspace-canvas';
import { EWorkPhase } from '@datatype/gridgio/type';
import type { IAnnotation, IArea, IDrawing, TAreaType } from '@datatype/gridgio/type';
import { useWorkspaceAnnotation, useWorkspaceView } from '@hook/gridgio/useWorkspace';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/gridgio/modal-slice';
import CanvasToolbar from '@design-system/canvas-toolbar';
import LoadingDots from '@design-system/loading-dots';
import FloatingControl from '@design-system/floating-control';
import PageNavigator from '@design-system/page-navigator';
import { ETheme } from '@datatype/type';
import { deserializeGridgioWork, getGridgioWorkFileName, saveGridgioWork } from '@util/gridgio-project';
import { createProjectPath } from '@constant/gridgio/route-path';
import { GRIDGIO_ISSUES } from '@constant/gridgio/issue';
import {
  useModifyWorkMutation,
  useWorkQuery,
  useAnnotationsQuery,
  useCreateAnnotationMutation,
} from '@hook/gridgio/useApi';
import AnnotationSideControl from '@component/gridgio/project/work/annotation-side-control';
import { useAnnotationPhoto } from '@hook/gridgio/useAnnotationPhoto';

const createSheetKey = (page: number) => `sheet${page}`;

// ############### styled (레이아웃 전용) ###############
const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  padding: ${size.PAGE_PADDING} ${size.PAGE_PADDING} ${size.PAGE_PADDING} ${size.PAGE_PADDING};
  display: flex;
  flex-flow: column nowrap;
`;

const WorkContentContainer = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
  gap: ${size.MARGIN_1};
`;

const WorkspaceContainer = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid ${color.GRAY_BOX_BORDER_COLOR};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const BottomContentContainer = styled.div`
  width: 100%;
  min-width: 0;
`;

const TopContentContainer = styled.div`
  position: absolute;
  top: ${size.MARGIN_1};
  right: ${size.MARGIN_1};
  left: ${size.MARGIN_1};
  z-index: 2;
`;

const FloatingControlContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
`;

const SideContentContainer = styled.div`
  display: flex;
  width: 250px;
  height: 100%;
  min-height: 0;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
`;

const SideControlContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  flex-shrink: 0;
  flex-flow: column nowrap;
  align-items: center;
  padding: 5px 10px;
  background-color: ${color.WHITE_1};
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_BOX_BORDER_COLOR};
  border-radius: ${size.DESIGN_SYSTEM_BUTTON_BORDER_RADIUS};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const WorkControlContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: ${size.MARGIN_1};
`;

const SideMenuContainer = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  width: 100%;
  z-index: 3;
`;

const CanvasContentContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const AnnotationContainer = styled.div`
  display: grid;
  flex: 1;
  min-width: 0;
  grid-template-columns: repeat(auto-fill, minmax(min(150px, 100%), 1fr));
  padding: 15px;
  gap: ${size.MARGIN_1};
  background-color: ${color.WHITE_1};
  border: ${size.BORDER_WIDTH_1} solid ${color.CYAN_4};
  border-radius: ${size.BORDER_RADIUS_3};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const AnnotationToolContainer = styled.div<{ $selected: boolean }>`
  display: flex;
  min-width: 0;
  height: 40px;
  align-items: center;
  justify-content: center;
  padding: 2px 5px;
  background-color: ${({ $selected }) => ($selected ? color.CYAN_2 : color.CYAN_4)};
  border-radius: ${size.BORDER_RADIUS_2};
  cursor: pointer;
`;

const ControlContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  padding: 15px;
  background-color: ${color.WHITE_1};
  border: 1px solid ${color.GRAY_BOX_BORDER_COLOR};
  border-radius: ${size.BORDER_RADIUS_3};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const DrawingSettingContainer = styled(ControlContainer)`
  align-items: center;
`;

const MenuControlContainer = styled(ControlContainer)`
  border-color: ${color.GRAY_2};
  box-shadow: ${color.SURFACE_SHADOW};
  width: 100%;
  min-width: 200px;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_2};
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_2};
`;

const MENU_ITEM_DESIGN = {
  box: { width: '100%', gap: size.MARGIN_1, alignX: 'left' as const, event: { cursor: 'pointer' as const } },
  text: { size: 'medium' as const, color: color.BLACK_2 },
  icon: { size: 'medium' as const, kind: 'outlined' as const, color: color.BLACK_2 },
};

const DRAWING_SETTING_ITEM_DESIGN = {
  box: {
    width: '120px',
    height: '40px',
    gap: '5px',
    backgroundColor: color.WHITE_1,
    border: true,
    borderWidth: size.BORDER_WIDTH_1,
    borderColor: color.CYAN_2,
    borderRadius: size.BORDER_RADIUS_2,
    event: { cursor: 'pointer' as const },
  },
  text: { size: 'medium' as const, color: color.CYAN_2 },
  icon: { size: 'small' as const, kind: 'outlined' as const, color: color.CYAN_2 },
};

const DRAWING_SETTING_COMPLETE_DESIGN = {
  box: {
    ...DRAWING_SETTING_ITEM_DESIGN.box,
    backgroundColor: color.CYAN_2,
    borderRadius: size.BORDER_RADIUS_1,
  },
  text: { size: 'medium' as const, color: color.WHITE_1 },
};

const DRAWING_SETTING_CLOSE_DESIGN = {
  box: {
    ...DRAWING_SETTING_ITEM_DESIGN.box,
    borderColor: color.RED_2,
    borderRadius: DRAWING_SETTING_COMPLETE_DESIGN.box.borderRadius,
  },
  text: { size: 'medium' as const, color: color.RED_2 },
};

const createDrawingSettingItemDesign = (active: boolean) => ({
  ...DRAWING_SETTING_ITEM_DESIGN,
  box: {
    ...DRAWING_SETTING_ITEM_DESIGN.box,
    backgroundColor: active ? color.CYAN_5 : color.WHITE_1,
  },
});

const ANNOTATION_TOOL_DESIGN = {
  box: { width: '100%' },
  text: {
    size: 'medium' as const,
    color: color.CYAN_2,
    bold: true,
    oneline: true,
  },
};

const Work: React.FC = () => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const { workKey = '' } = useParams();
  // TODO: 온라인 작업 흐름을 정리할 때 온라인·오프라인 분기와 로컬 작업 경로를 함께 재검토한다.
  const isOnlineWork = Boolean(workKey);
  const workQuery = useWorkQuery(workKey);
  const annotationQuery = useAnnotationsQuery(workKey);
  const { mutateAsync: createAnnotation } = useCreateAnnotationMutation();
  const { mutateAsync: modifyWork } = useModifyWorkMutation();
  const drawingFileInputRef = useRef<HTMLInputElement>(null);
  const projectFileInputRef = useRef<HTMLInputElement>(null);
  const loadedSourceKeyRef = useRef<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isSettingMenuOpen, setIsSettingMenuOpen] = useState(false);
  const [isBottomControlOpen, setIsBottomControlOpen] = useState(true);
  const [workPhase, setWorkPhase] = useState(isOnlineWork ? EWorkPhase.DRAWING_SETUP : EWorkPhase.PROJECT_SETUP);
  const [drawing, setDrawing] = useState<IDrawing | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [localSheetAnnotation, setLocalSheetAnnotation] = useState<Record<string, IAnnotation[]>>({});
  const sheetAnnotation = useMemo(() => {
    if (!isOnlineWork) return localSheetAnnotation;
    const sheets: Record<string, IAnnotation[]> = {};
    for (const item of annotationQuery.data?.data ?? []) {
      (sheets[item.sheetId] ??= []).push({
        id: item.key,
        issueId: item.issueId,
        mark: item.mark,
        property: item.property,
        memo: item.memo,
      });
    }
    return sheets;
  }, [isOnlineWork, annotationQuery.data, localSheetAnnotation]);
  const [rotation, setRotation] = useState(0);
  const [areaType, setAreaType] = useState<TAreaType | null>(null);
  const [workArea, setWorkArea] = useState<IArea | null>(null);
  const [keyPlanArea, setKeyPlanArea] = useState<IArea | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isWorkSaving, setIsWorkSaving] = useState(false);
  const {
    view: workspaceView,
    setView: setWorkspaceView,
    moveActive: isMoveActive,
    resetView: resetWorkspaceView,
    disableMove: disableWorkspaceMove,
    handleToolbarClick: handleCanvasToolbarClick,
  } = useWorkspaceView();
  const {
    drawingIssueId,
    selectedAnnotationId,
    selectedMemoId,
    dialogMode: annotationDialogMode,
    reset: resetAnnotationInteraction,
    toggleDrawingIssue,
    completeDrawing,
    openDialog: openAnnotationDialog,
    completeDialog: completeAnnotationDialog,
    selectAnnotation: handleAnnotationSelect,
  } = useWorkspaceAnnotation();
  const annotation = sheetAnnotation[createSheetKey(currentPage)] ?? [];
  const selectedAnnotation = annotation.find((item) => item.id === selectedAnnotationId) ?? null;
  const photo = useAnnotationPhoto(workKey, isOnlineWork ? (selectedAnnotation?.id ?? '') : '');
  const selectedIssue = GRIDGIO_ISSUES.find((issue) => issue.id === selectedAnnotation?.issueId);

  const handlePageChange = useCallback((count: number) => {
    setTotalPage(count);
    setCurrentPage((page) => Math.min(page, count));
  }, []);

  useEffect(() => {
    if (!isSettingMenuOpen) return;

    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) {
        setIsSettingMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleOutsidePointerDown, true);
    return () => document.removeEventListener('pointerdown', handleOutsidePointerDown, true);
  }, [isSettingMenuOpen]);

  useEffect(
    () => () => {
      if (drawing) URL.revokeObjectURL(drawing.url);
    },
    [drawing],
  );

  useEffect(() => {
    resetWorkspaceView();
  }, [currentPage, drawing?.url, resetWorkspaceView, rotation, workPhase]);

  useEffect(() => {
    const work = workQuery.data?.data;
    const source = work?.plan?.source;
    // TODO: 같은 Source를 사용하는 다른 Work로 이동해도 설정을 다시 불러오도록 초기화 기준을 workKey까지 포함한다.
    if (!work || !source?.sourceLink || loadedSourceKeyRef.current === source.key) return;
    loadedSourceKeyRef.current = source.key;

    fetch(source.sourceLink)
      .then((response) => {
        if (!response.ok) throw new Error('source download failed');
        return response.blob();
      })
      .then((blob) => {
        const file = new File([blob], source.filename, { type: source.contentType });
        setDrawing({
          file,
          url: URL.createObjectURL(file),
          name: file.name,
          type: file.type === 'application/pdf' ? 'pdf' : 'image',
        });
        const configuration = work.configuration;
        const savedRotation = configuration.rotation;
        const savedWorkArea = configuration.workArea;
        const savedKeyPlanArea = configuration.keyPlanArea;
        setRotation(typeof savedRotation === 'number' ? savedRotation : 0);
        setWorkArea((savedWorkArea as IArea | null | undefined) ?? null);
        setKeyPlanArea((savedKeyPlanArea as IArea | null | undefined) ?? null);
        setWorkPhase(Object.keys(configuration).length > 0 ? EWorkPhase.ANNOTATION : EWorkPhase.DRAWING_SETUP);
      })
      .catch((error) => {
        loadedSourceKeyRef.current = null;
        console.error('Gridgio source load failed', error);
      });
  }, [workQuery.data]);

  const handleDrawingFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setDrawing({
        file,
        url: URL.createObjectURL(file),
        name: file.name,
        type: file.type === 'application/pdf' ? 'pdf' : 'image',
      });
      setCurrentPage(1);
      setTotalPage(1);
      setLocalSheetAnnotation({});
      setRotation(0);
      setAreaType(null);
      setWorkArea(null);
      setKeyPlanArea(null);
      resetAnnotationInteraction();
      setWorkPhase(EWorkPhase.DRAWING_SETUP);
      setIsSettingMenuOpen(false);
    }
    event.target.value = '';
  };

  const handleProjectFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const work = await deserializeGridgioWork(file);
      setDrawing(work.drawing);
      setCurrentPage(1);
      setTotalPage(work.totalPage);
      setLocalSheetAnnotation(work.annotation);
      setRotation(work.rotation);
      setAreaType(null);
      setWorkArea(work.workArea);
      setKeyPlanArea(work.keyPlanArea);
      resetAnnotationInteraction();
      setWorkPhase(EWorkPhase.ANNOTATION);
      setIsSettingMenuOpen(false);
    } catch (error) {
      console.error('Gridgio project load failed', error);
      window.alert(error instanceof Error ? error.message : '프로젝트를 열지 못했습니다.');
    }
  };

  const handleRotate = (degree: number) => {
    setRotation((current) => (current + degree + 360) % 360);
    setAreaType(null);
    setWorkArea(null);
    setKeyPlanArea(null);
  };

  const handleAreaChange = (type: TAreaType, area: IArea) => {
    if (type === 'drawing') setWorkArea(area);
    if (type === 'key-plan') setKeyPlanArea(area);
  };

  const handlePageNavigationClick = (page: number) => {
    if (page < 1 || page > totalPage) return;
    setAreaType(null);
    handleAnnotationSelect(null);
    setCurrentPage(page);
  };

  const saveOnlineWork = useCallback(
    () => modifyWork({ workKey, configuration: { rotation, workArea, keyPlanArea } }),
    [modifyWork, workKey, rotation, workArea, keyPlanArea],
  );

  const handleDrawingSettingComplete = async () => {
    if (isWorkSaving) return;
    setAreaType(null);
    if (!isOnlineWork) {
      setWorkPhase(EWorkPhase.ANNOTATION);
      return;
    }

    setIsWorkSaving(true);
    try {
      await saveOnlineWork();
      setWorkPhase(EWorkPhase.ANNOTATION);
    } catch (error) {
      console.error('Gridgio work configuration save failed', error);
      window.alert('도면 설정을 저장하지 못했습니다.');
    } finally {
      setIsWorkSaving(false);
    }
  };

  const handleAnnotationComplete = async (issueId: string, mark: IAnnotation['mark']) => {
    if (isOnlineWork && !annotationQuery.isSuccess) return;
    const sheetId = createSheetKey(currentPage);
    try {
      let annotationId: string;
      if (isOnlineWork) {
        const response = await createAnnotation({ workKey, sheetId, issueId, mark });
        annotationId = response.data.key;
      } else {
        annotationId = uuid4();
        const item: IAnnotation = { id: annotationId, issueId, mark, memo: [] };
        setLocalSheetAnnotation((current) => ({ ...current, [sheetId]: [...(current[sheetId] ?? []), item] }));
      }
      completeDrawing(annotationId);
    } catch (error) {
      console.error('Gridgio annotation save failed', error);
      window.alert('기록을 저장하지 못했습니다. 다시 시도해주세요.');
    }
  };

  useEffect(() => {
    if (!selectedAnnotation || !annotationDialogMode || annotationDialogMode === 'photo') return;
    dispatch(
      modalActions.showDialogModal({
        type: 'annotation',
        data: {
          workKey,
          sheetId: createSheetKey(currentPage),
          annotationId: selectedAnnotation.id,
          mode: annotationDialogMode,
          memoId: selectedMemoId,
        },
      }),
    );
    completeAnnotationDialog();
  }, [
    dispatch,
    workKey,
    currentPage,
    selectedAnnotation,
    annotationDialogMode,
    selectedMemoId,
    completeAnnotationDialog,
  ]);

  const handleExport = async () => {
    if (!drawing || isExporting) return;
    if (isOnlineWork && !annotationQuery.isSuccess) {
      dispatch(modalActions.showDialogModal({ type: 'plain-text', data: '기록을 불러온 뒤 다시 시도해주세요.' }));
      return;
    }
    setIsExporting(true);
    try {
      const result = await saveGridgioWork({
        drawing,
        rotation,
        workArea,
        keyPlanArea,
        annotation: sheetAnnotation,
        totalPage,
      });
      const filename = getGridgioWorkFileName(drawing.name);
      if (result?.destination === 'downloads') {
        dispatch(modalActions.showDialogModal({ type: 'file-saved', data: { filename } }));
      }
      if (result?.destination === 'share') {
        dispatch(modalActions.showDialogModal({ type: 'plain-text', data: `작업 파일을 내보냈습니다.\n${filename}` }));
      }
      setIsSettingMenuOpen(false);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Gridgio work export failed', error);
      dispatch(
        modalActions.showDialogModal({ type: 'plain-text', data: '작업 파일을 내보내지 못했어요. 다시 시도해주세요.' }),
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => navigate(createProjectPath('project', workQuery.data?.data?.project?.key));

  const annotationTools = GRIDGIO_ISSUES.map((issue) => {
    const isSelected = drawingIssueId === issue.id;
    return (
      <AnnotationToolContainer
        key={issue.id}
        $selected={isSelected}
        onClick={() => {
          disableWorkspaceMove();
          toggleDrawingIssue(issue.id);
        }}>
        <TextBlock
          text={issue.name}
          design={{
            ...ANNOTATION_TOOL_DESIGN,
            text: { ...ANNOTATION_TOOL_DESIGN.text, color: isSelected ? color.WHITE_1 : color.CYAN_2 },
          }}
        />
      </AnnotationToolContainer>
    );
  });

  const WorkMenuItems = (
    <MenuControlContainer>
      {!isOnlineWork && (
        <IconTextBlock
          icon='add'
          text='새 작업 만들기'
          design={MENU_ITEM_DESIGN}
          click={{ handler: () => drawingFileInputRef.current?.click() }}
        />
      )}
      {!isOnlineWork && (
        <IconTextBlock
          icon='folder-open'
          text='작업 열기'
          design={MENU_ITEM_DESIGN}
          click={{ handler: () => projectFileInputRef.current?.click() }}
        />
      )}
      <IconTextBlock
        icon='download'
        text={isExporting ? '내보내는 중...' : '내보내기'}
        design={MENU_ITEM_DESIGN}
        click={{ handler: handleExport }}
      />
      <IconTextBlock icon='share' text='공유하기' design={MENU_ITEM_DESIGN} />
      <IconTextBlock icon='info' text='현재 작업 정보' design={MENU_ITEM_DESIGN} />
      <IconTextBlock icon='close' text='작업 닫기' design={MENU_ITEM_DESIGN} click={{ handler: handleClose }} />
    </MenuControlContainer>
  );

  return (
    <Wrapper>
      <WorkContentContainer>
        <WorkspaceContainer>
          {workPhase === EWorkPhase.PROJECT_SETUP && (
            <TopContentContainer>
              <FloatingControlContainer ref={menuRef}>
                <FloatingControl
                  icon='menu'
                  open={isSettingMenuOpen}
                  theme={ETheme.CYAN}
                  size='large'
                  direction='column'
                  gap={size.MARGIN_1}
                  handleClick={() => setIsSettingMenuOpen((isOpen) => !isOpen)}>
                  {WorkMenuItems}
                </FloatingControl>
              </FloatingControlContainer>
            </TopContentContainer>
          )}
          <CanvasContentContainer>
            <input
              ref={drawingFileInputRef}
              type='file'
              accept='application/pdf,image/jpeg,image/png,image/webp'
              hidden
              onChange={handleDrawingFileChange}
            />
            <input
              ref={projectFileInputRef}
              type='file'
              accept='.giw,application/vnd.gridgio.work+zip'
              hidden
              onChange={handleProjectFileChange}
            />
            {!isOnlineWork && workPhase === EWorkPhase.PROJECT_SETUP && (
              <IconTextBlock
                icon='add'
                text='새 프로젝트 만들기'
                design={{
                  box: {
                    gap: size.MARGIN_1,
                    padding: size.MARGIN_2,
                    border: true,
                    borderWidth: size.BORDER_WIDTH_1,
                    borderStyle: 'dashed',
                    borderColor: color.GRAY_2,
                    borderRadius: size.BORDER_RADIUS_2,
                    event: { cursor: 'pointer' },
                  },
                  text: { size: 'large', color: color.GRAY_1 },
                  icon: { size: 'medium', kind: 'outlined', color: color.GRAY_1 },
                }}
                click={{ handler: () => drawingFileInputRef.current?.click() }}
              />
            )}
            {isOnlineWork && annotationQuery.isLoading && <LoadingDots />}
            {isOnlineWork && annotationQuery.isError && (
              <IconTextBlock
                icon='rotate-left'
                text='기록을 불러오지 못했습니다. 다시 시도'
                design={{
                  box: { event: { cursor: 'pointer' } },
                  text: { size: 'small', color: color.RED_2 },
                  icon: { size: 'small', color: color.RED_2 },
                }}
                click={{ handler: () => void annotationQuery.refetch() }}
              />
            )}
            {drawing && (!isOnlineWork || annotationQuery.isSuccess) && (
              <WorkspaceCanvas
                drawing={drawing}
                page={currentPage}
                rotation={rotation}
                areaType={workPhase === EWorkPhase.DRAWING_SETUP ? areaType : null}
                workArea={workPhase === EWorkPhase.DRAWING_SETUP ? workArea : null}
                keyPlanArea={workPhase === EWorkPhase.DRAWING_SETUP ? keyPlanArea : null}
                cropArea={workPhase === EWorkPhase.ANNOTATION ? workArea : null}
                selectedIssueId={workPhase === EWorkPhase.ANNOTATION ? drawingIssueId : null}
                selectedAnnotationId={selectedAnnotationId}
                annotationSelectionEnabled={workPhase === EWorkPhase.ANNOTATION}
                annotation={annotation}
                view={workspaceView}
                moveActive={isMoveActive}
                handleViewChange={setWorkspaceView}
                handleAreaChange={handleAreaChange}
                handleAnnotationComplete={handleAnnotationComplete}
                handleAnnotationSelect={handleAnnotationSelect}
                handlePageChange={handlePageChange}
              />
            )}
          </CanvasContentContainer>
        </WorkspaceContainer>
        {(workPhase === EWorkPhase.ANNOTATION || workPhase === EWorkPhase.DRAWING_SETUP) && (
          <SideContentContainer>
            <SideControlContainer>
              {workPhase === EWorkPhase.ANNOTATION && (
                <>
                  <WorkControlContainer ref={menuRef}>
                    <TextBlock
                      text={workQuery.data?.data?.name || drawing?.name || '작업'}
                      design={{
                        box: { width: '100%', alignX: 'left' },
                        text: { size: 'medium', color: color.BLACK_2, bold: true, oneline: true },
                      }}
                    />
                    <IconBlock
                      icon='ellipsis'
                      design={{
                        box: {
                          width: '35px',
                          height: '25px',
                          backgroundColor: isSettingMenuOpen ? color.GRAY_3 : undefined,
                          borderRadius: size.BORDER_RADIUS_1,
                          event: { cursor: 'pointer' },
                        },
                        icon: { size: 'small', kind: 'outlined', color: color.BLACK_2 },
                      }}
                      click={{ handler: () => setIsSettingMenuOpen((isOpen) => !isOpen) }}
                    />
                    {isSettingMenuOpen && <SideMenuContainer>{WorkMenuItems}</SideMenuContainer>}
                  </WorkControlContainer>
                  <Line direction='horizontal' width='100%' color={color.GRAY_2} margin={`5px 0px 5px 0px`} />
                </>
              )}
              {drawing && (
                <PageNavigator
                  currentPage={currentPage}
                  totalPage={totalPage}
                  width='100%'
                  border={false}
                  handleChange={handlePageNavigationClick}
                />
              )}
              {workPhase === EWorkPhase.ANNOTATION && drawing && (
                <Line direction='horizontal' width='100%' color={color.GRAY_2} margin={`0px 0px 5px 0px`} />
              )}
              {workPhase === EWorkPhase.ANNOTATION && (
                <CanvasToolbar
                  items={['zoom', 'fit', 'move']}
                  moveActive={isMoveActive}
                  zoomRatio={Math.round(workspaceView.zoom * 100)}
                  theme={ETheme.GRAY}
                  size='medium'
                  width='100%'
                  border={false}
                  handleClick={(action) => {
                    if (action === 'move' && !isMoveActive) handleAnnotationSelect(null);
                    handleCanvasToolbarClick(action);
                  }}
                />
              )}
            </SideControlContainer>
            {/* 우측 키플랜 미리보기는 UI가 확정되면 다시 연결한다. */}
            {workPhase === EWorkPhase.ANNOTATION && selectedAnnotation && (
              <>
                <Gap height={size.MARGIN_1} />
                <AnnotationSideControl
                  issueName={selectedIssue?.name ?? ''}
                  property={selectedAnnotation.property}
                  memo={selectedAnnotation.memo}
                  photoCount={photo.photos.length}
                  photos={photo.photos}
                  photoMessage={
                    photo.message ||
                    (photo.photoQuery.isLoading
                      ? '사진을 불러오는 중입니다.'
                      : photo.photoQuery.isError
                        ? '사진을 불러오지 못했습니다.'
                        : undefined)
                  }
                  photoBusy={photo.busy || !isOnlineWork}
                  photoRetryable={photo.retryable}
                  handlePhotoClick={() => void photo.capture()}
                  handlePhotoRetryClick={() => void photo.retry()}
                  handlePhotoCancelClick={() => void photo.cancel()}
                  handlePhotoDeleteClick={(key) => void photo.remove(key)}
                  handlePhotoOpenClick={(url) =>
                    dispatch(modalActions.showDialogModal({ type: 'source-preview', data: { url, type: 'image' } }))
                  }
                  handlePropertyClick={() => openAnnotationDialog('property')}
                  handleMemoAddClick={() => openAnnotationDialog('memo')}
                  handleMemoListClick={() => openAnnotationDialog('memo-list')}
                  handleMemoClick={(memo) => openAnnotationDialog('memo', memo.stroke, memo.id)}
                  handleDeleteClick={() => openAnnotationDialog('delete')}
                />
              </>
            )}
          </SideContentContainer>
        )}
      </WorkContentContainer>
      {workPhase !== EWorkPhase.PROJECT_SETUP && (
        <BottomContainer position='relative' height='auto' margin={`${size.MARGIN_1} 0 0`}>
          <BottomContentContainer>
            {workPhase === EWorkPhase.DRAWING_SETUP && (
              <FloatingControl
                icon='setting'
                open={isBottomControlOpen}
                theme={ETheme.CYAN}
                size='large'
                direction='row'
                gap={size.MARGIN_1}
                width='100%'
                handleClick={() => setIsBottomControlOpen((isOpen) => !isOpen)}>
                <DrawingSettingContainer>
                  <IconTextBlock
                    icon='rotate-left'
                    text='90°'
                    design={DRAWING_SETTING_ITEM_DESIGN}
                    click={{ handler: () => handleRotate(-90) }}
                  />
                  <Gap width={size.MARGIN_1} />
                  <IconTextBlock
                    icon='rotate-right'
                    text='90°'
                    design={DRAWING_SETTING_ITEM_DESIGN}
                    click={{ handler: () => handleRotate(90) }}
                  />
                  <Gap width={size.MARGIN_1} />
                  <Line direction='vertical' height='25px' color={color.GRAY_2} />
                  <Gap width={size.MARGIN_1} />
                  <IconTextBlock
                    icon='scan'
                    text='도면 영역'
                    design={createDrawingSettingItemDesign(areaType === 'drawing')}
                    click={{ handler: () => setAreaType('drawing') }}
                  />
                  <Gap width={size.MARGIN_2} />
                  <IconTextBlock
                    icon='square-dashed'
                    text='키플랜 영역'
                    design={createDrawingSettingItemDesign(areaType === 'key-plan')}
                    click={{ handler: () => setAreaType('key-plan') }}
                  />
                  <Gap width={size.MARGIN_1} />
                  <Line direction='vertical' height='25px' color={color.GRAY_2} />
                  <Gap width={size.MARGIN_1} />
                  <TextBlock
                    text='설정 완료'
                    design={DRAWING_SETTING_COMPLETE_DESIGN}
                    click={{ handler: () => void handleDrawingSettingComplete() }}
                  />
                  <Gap width={size.MARGIN_1} />
                  <TextBlock text='작업 닫기' design={DRAWING_SETTING_CLOSE_DESIGN} click={{ handler: handleClose }} />
                </DrawingSettingContainer>
              </FloatingControl>
            )}
            {workPhase === EWorkPhase.ANNOTATION && <AnnotationContainer>{annotationTools}</AnnotationContainer>}
          </BottomContentContainer>
        </BottomContainer>
      )}
    </Wrapper>
  );
};

export default Work;
